import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, MapPin, Mail, Phone, User, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { es as esLocale, enUS } from 'date-fns/locale';

import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';

import { useI18n } from '../lib/i18n';
import { TIDS } from '../constants/testIds';
import {
  createBooking,
  createCheckoutSession,
  createPaypalCheckout,
  fetchMemberships,
  fetchExtras,
} from '../lib/api';

const TIMES = [];
for (let h = 8; h <= 20; h++) {
  TIMES.push(`${String(h).padStart(2, '0')}:00`);
  if (h !== 20) TIMES.push(`${String(h).padStart(2, '0')}:30`);
}

const initial = {
  full_name: '',
  email: '',
  phone: '',
  pickup_address: '',
  delivery_address: '',
  pickup_time: '',
  membership: '',
  detergent: '',
  notes: '',
  extras: [],
};

const BookingForm = ({ selectedPlan }) => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState();
  const [plans, setPlans] = useState([]);
  const [extras, setExtras] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    fetchMemberships().then(setPlans).catch(() => {});
    fetchExtras().then(setExtras).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedPlan) {
      setForm((f) => ({ ...f, membership: selectedPlan }));
      const el = document.getElementById('booking');
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedPlan]);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (k) => (e) => update(k)(e.target.value);

  const submit = async (e, provider = 'stripe') => {
    e.preventDefault();
    if (!date) {
      toast.error(t.booking.errorTitle, { description: t.booking.pickDate });
      return;
    }
    if (!form.membership) {
      toast.error(t.booking.errorTitle, { description: t.booking.pickPlan });
      return;
    }
    if (!form.pickup_time) {
      toast.error(t.booking.errorTitle, { description: t.booking.pickTime });
      return;
    }
    if (!form.detergent) {
      toast.error(t.booking.errorTitle, { description: t.booking.pickDetergent });
      return;
    }
    try {
      setSubmitting(provider);
      // Attribution: if user landed with ?src=email_upsell, we captured it in
      // localStorage in App.jsx. Include it on booking creation so admin/stats
      // can report email→Plus/Premium conversion. Cleared after successful
      // checkout redirect.
      const attribution = localStorage.getItem('pls_attribution') || undefined;
      const booking = await createBooking({
        ...form,
        pickup_date: format(date, 'yyyy-MM-dd'),
        ...(attribution ? { source: attribution } : {}),
      });
      if (attribution) localStorage.removeItem('pls_attribution');
      const factory = provider === 'paypal' ? createPaypalCheckout : createCheckoutSession;
      const { url } = await factory(booking.id, window.location.origin);
      toast.success(t.booking.redirecting);
      window.location.href = url;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail.map((d) => d.msg).join(', ')
          : t.booking.errorDesc);
      toast.error(t.booking.errorTitle, { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    'w-full bg-[#F5F5F7] border-2 border-transparent focus:bg-white focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 rounded-2xl px-5 py-4 text-[15px] text-[#1D1D1F] placeholder:text-[#86868B] transition-all outline-none';

  const fieldLabel = 'block text-xs font-semibold tracking-wider uppercase text-[#86868B] mb-2';

  return (
    <section
      id="booking"
      data-testid={TIDS.bookingSection}
      className="py-24 sm:py-32 bg-[#F5F5F7]"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1D1D1F] leading-tight">
            {t.booking.title}
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-[#86868B] font-medium leading-relaxed">
            {t.booking.subtitle}
          </p>
        </motion.div>

        <motion.form
          data-testid={TIDS.bookingForm}
          onSubmit={submit}
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mt-12 sm:mt-16 bg-white rounded-[2rem] p-6 sm:p-10 lg:p-12 shadow-[0_18px_60px_rgba(0,0,0,0.05)] border border-black/5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            <div>
              <label className={fieldLabel}>
                <User className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.fullName}
              </label>
              <input
                data-testid={TIDS.fullName}
                value={form.full_name}
                onChange={onChange('full_name')}
                required
                minLength={2}
                className={inputBase}
                placeholder="María Rodríguez"
              />
            </div>
            <div>
              <label className={fieldLabel}>
                <Mail className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.email}
              </label>
              <input
                data-testid={TIDS.email}
                value={form.email}
                onChange={onChange('email')}
                required
                type="email"
                className={inputBase}
                placeholder="maria@email.com"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={fieldLabel}>
                <Phone className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.phone}
              </label>
              <input
                data-testid={TIDS.phone}
                value={form.phone}
                onChange={onChange('phone')}
                required
                type="tel"
                className={inputBase}
                placeholder="+1 555 123 4567"
              />
            </div>
            <div>
              <label className={fieldLabel}>
                <MapPin className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.pickup}
              </label>
              <input
                data-testid={TIDS.pickupAddress}
                value={form.pickup_address}
                onChange={onChange('pickup_address')}
                required
                className={inputBase}
                placeholder="123 Main St, Miami FL"
              />
            </div>
            <div>
              <label className={fieldLabel}>
                <MapPin className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.delivery}
              </label>
              <input
                data-testid={TIDS.deliveryAddress}
                value={form.delivery_address}
                onChange={onChange('delivery_address')}
                required
                className={inputBase}
                placeholder="123 Main St, Miami FL"
              />
            </div>

            <div>
              <label className={fieldLabel}>
                <CalendarIcon className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.date}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid={TIDS.dateTrigger}
                    className={`${inputBase} text-left flex items-center justify-between`}
                  >
                    <span className={date ? 'text-[#1D1D1F]' : 'text-[#86868B]'}>
                      {date
                        ? format(date, 'PPP', { locale: lang === 'es' ? esLocale : enUS })
                        : t.booking.pickDate}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-[#86868B]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 rounded-2xl border border-black/10 shadow-xl"
                  align="start"
                >
                  <div data-testid={TIDS.dateCalendar}>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      locale={lang === 'es' ? esLocale : enUS}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today;
                      }}
                      initialFocus
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className={fieldLabel}>
                <Clock className="inline w-3 h-3 mr-1 -mt-0.5" /> {t.booking.time}
              </label>
              <Select value={form.pickup_time} onValueChange={update('pickup_time')}>
                <SelectTrigger
                  data-testid={TIDS.timeSelect}
                  className={`${inputBase} h-auto justify-between`}
                >
                  <SelectValue placeholder={t.booking.pickTime} />
                </SelectTrigger>
                <SelectContent className="rounded-2xl max-h-72">
                  {TIMES.map((tm) => (
                    <SelectItem key={tm} value={tm} className="rounded-xl">
                      {tm}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <label className={fieldLabel}>{t.booking.plan}</label>
              <Select value={form.membership} onValueChange={update('membership')}>
                <SelectTrigger
                  data-testid={TIDS.membershipSelectField}
                  className={`${inputBase} h-auto justify-between`}
                >
                  <SelectValue placeholder={t.booking.pickPlan} />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="rounded-xl">
                      {p.name} — ${p.price}
                      {lang === 'es' ? p.frequency_es : p.frequency_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <label className={fieldLabel}>{t.booking.detergent}</label>
              <Select value={form.detergent} onValueChange={update('detergent')}>
                <SelectTrigger
                  data-testid={TIDS.detergentSelect}
                  className={`${inputBase} h-auto justify-between`}
                >
                  <SelectValue placeholder={t.booking.pickDetergent} />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="standard" className="rounded-xl">
                    {t.booking.detergentStandard}
                  </SelectItem>
                  <SelectItem value="hypoallergenic" className="rounded-xl">
                    {t.booking.detergentHypo}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Extras */}
            <div className="sm:col-span-2" data-testid="extras-section">
              <label className={fieldLabel}>{t.booking.extrasTitle}</label>
              <p className="text-sm text-[#86868B] mb-3 -mt-1">{t.booking.extrasSubtitle}</p>
              {extras.length === 0 ? (
                <p className="text-sm text-[#86868B] italic">{t.booking.extrasEmpty}</p>
              ) : (
                <div className="space-y-3">
                  {extras.map((x) => {
                    const isSelected = form.extras.includes(x.id);
                    const name = lang === 'en' ? x.name_en : x.name_es;
                    const description = lang === 'en' ? x.description_en : x.description_es;
                    // Compute days remaining if this extra has an expiration.
                    let daysLeft = null;
                    if (x.expires_at) {
                      const expMs = new Date(x.expires_at).getTime();
                      if (!Number.isNaN(expMs)) {
                        daysLeft = Math.ceil((expMs - Date.now()) / 86400000);
                      }
                    }
                    const toggle = () => {
                      setForm((f) => {
                        // Build a set of currently selected ids
                        const set = new Set(f.extras);
                        if (isSelected) {
                          set.delete(x.id);
                        } else {
                          // Adding this extra:
                          // 1) If it's a combo, drop any of its included items.
                          // 2) Drop any *other* combo whose includes overlap with it
                          //    (also if selecting an individual item that's inside a combo).
                          if (Array.isArray(x.includes)) {
                            x.includes.forEach((eid) => set.delete(eid));
                          }
                          extras.forEach((other) => {
                            if (other.id === x.id) return;
                            if (Array.isArray(other.includes)) {
                              const overlaps = other.includes.some(
                                (eid) => eid === x.id || (Array.isArray(x.includes) && x.includes.includes(eid))
                              );
                              if (overlaps) set.delete(other.id);
                            }
                          });
                          set.add(x.id);
                        }
                        return { ...f, extras: Array.from(set) };
                      });
                    };
                    return (
                      <button
                        type="button"
                        key={x.id}
                        data-testid={`extra-${x.id}`}
                        onClick={toggle}
                        aria-pressed={isSelected}
                        className={`relative w-full text-left rounded-2xl border-2 px-5 py-4 transition-all flex items-center gap-4 ${
                          isSelected
                            ? 'bg-white border-[#0071E3] ring-4 ring-[#0071E3]/10 shadow-sm'
                            : 'bg-[#F5F5F7] border-transparent hover:border-[#0071E3]/30'
                        }`}
                      >
                        {x.popular && (
                          <span
                            data-testid={`extra-${x.id}-popular`}
                            className="absolute -top-2 right-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0071E3] text-white shadow-sm"
                          >
                            ★ {lang === 'en' ? 'Popular' : 'Popular'}
                          </span>
                        )}
                        {daysLeft !== null && (
                          <span
                            data-testid={`extra-${x.id}-expires`}
                            className="absolute -top-2 left-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500 text-white shadow-sm"
                          >
                            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <circle cx="12" cy="12" r="9" />
                              <path strokeLinecap="round" d="M12 7v5l3 2" />
                            </svg>
                            {t.booking.extrasEndsIn(daysLeft)}
                          </span>
                        )}
                        <span
                          className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-[#0071E3] border-[#0071E3] text-white'
                              : 'bg-white border-[#D1D1D6]'
                          }`}
                          aria-hidden="true"
                        >
                          {isSelected && (
                            <svg viewBox="0 0 20 20" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l3.5 3.5L15 7" />
                            </svg>
                          )}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] font-semibold text-[#1D1D1F] leading-snug">{name}</span>
                          {description && (
                            <span className="block text-[13px] text-[#86868B] mt-0.5 leading-snug">{description}</span>
                          )}
                          {(lang === 'en' ? x.savings_en : x.savings_es) && (
                            <span className="inline-flex items-center gap-1 mt-2 text-[12px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="9" />
                                <path strokeLinecap="round" d="M12 7v5l3 2" />
                              </svg>
                              {lang === 'en' ? x.savings_en : x.savings_es}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-[15px] font-bold text-[#0071E3] tabular-nums">
                          +${x.price.toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className={fieldLabel}>{t.booking.notes}</label>
              <textarea
                data-testid={TIDS.notes}
                value={form.notes}
                onChange={onChange('notes')}
                rows={3}
                className={`${inputBase} resize-none`}
                placeholder="..."
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center gap-4">
            {(() => {
              const plan = plans.find((p) => p.id === form.membership);
              const extrasTotal = extras
                .filter((x) => form.extras.includes(x.id))
                .reduce((s, x) => s + x.price, 0);
              const total = (plan?.price || 0) + extrasTotal;
              if (!plan) return null;
              const freq = lang === 'en' ? plan.frequency_en : plan.frequency_es;
              return (
                <div
                  data-testid="booking-total"
                  className="w-full sm:w-auto bg-white rounded-2xl px-6 py-3 flex items-center gap-6 text-[15px] shadow-sm"
                >
                  <div className="flex flex-col leading-tight">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#86868B]">
                      {t.booking.totalLabel}
                    </span>
                    <span className="text-2xl font-extrabold text-[#1D1D1F] tabular-nums leading-none mt-1">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-[13px] text-[#86868B] leading-snug border-l border-[#EDEDF0] pl-6">
                    <span className="font-semibold text-[#1D1D1F]">{plan.name}</span>
                    <span> · ${plan.price.toFixed(2)}{freq}</span>
                    {extrasTotal > 0 && (
                      <div className="text-[#0071E3] font-semibold mt-0.5">
                        + ${extrasTotal.toFixed(2)} en extras
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <button
              data-testid={TIDS.submit}
              type="submit"
              disabled={!!submitting}
              className="w-full sm:w-auto bg-[#0071E3] hover:bg-[#0077ED] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-full px-12 py-4 text-base transition-transform hover:scale-[1.03] active:scale-95 shadow-sm inline-flex items-center justify-center gap-2"
            >
              {submitting === 'stripe' ? (
                t.booking.sending
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4">
                    <rect x="2" y="6" width="20" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {t.booking.payCard}
                </>
              )}
            </button>

            <div className="flex items-center gap-3 w-full max-w-xs">
              <div className="h-px flex-1 bg-black/10" />
              <span className="text-xs uppercase tracking-wider text-[#86868B] font-semibold">
                {t.booking.orPay}
              </span>
              <div className="h-px flex-1 bg-black/10" />
            </div>

            <button
              data-testid="booking-submit-paypal"
              type="button"
              onClick={(e) => submit(e, 'paypal')}
              disabled={!!submitting}
              className="w-full sm:w-auto bg-[#FFC439] hover:bg-[#F0B71E] disabled:opacity-60 disabled:cursor-not-allowed text-[#003087] font-bold rounded-full px-12 py-4 text-base transition-transform hover:scale-[1.03] active:scale-95 shadow-sm inline-flex items-center justify-center gap-2"
            >
              {submitting === 'paypal' ? (
                t.booking.sending
              ) : (
                <>
                  <span className="text-[15px]">
                    <span className="font-extrabold text-[#003087]">Pay</span>
                    <span className="font-extrabold text-[#009cde]">Pal</span>
                  </span>
                </>
              )}
            </button>

            <p className="text-xs text-[#86868B] mt-1">{t.booking.paySubtitle}</p>
          </div>
        </motion.form>
      </div>

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent
          data-testid={TIDS.successDialog}
          className="rounded-[2rem] border border-black/10 p-8 max-w-md"
        >
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#0071E3]/10 flex items-center justify-center mb-4">
              <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-[#0071E3]">
                <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <DialogTitle className="text-center text-2xl font-bold tracking-tight text-[#1D1D1F]">
              {t.booking.successTitle}
            </DialogTitle>
            <DialogDescription className="text-center text-[#86868B] text-[15px] leading-relaxed">
              {t.booking.successDesc}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <button
              data-testid={TIDS.successClose}
              onClick={() => setSuccessOpen(false)}
              className="bg-[#0071E3] hover:bg-[#0077ED] text-white font-semibold rounded-full px-8 py-3 transition-transform hover:scale-[1.03] active:scale-95"
            >
              {t.booking.successCta}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BookingForm;
